import type { ReactNode } from "react";
import { clsx } from "clsx";
import { Text } from "../typography";

export interface SimpleTableColumn<T> {
  key: string;
  label: string;
  className?: string;
  headerClassName?: string;
  render: (item: T) => ReactNode;
}

export interface SimpleTableProps<T> {
  columns: SimpleTableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  onRowClick?: (item: T) => void;
  footer?: ReactNode;
  className?: string;
}

export function SimpleTable<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
  footer,
  className,
}: SimpleTableProps<T>) {
  return (
    <div className={clsx("overflow-hidden rounded-xl border border-borderDefault bg-bgWhite", className)}>
      <div className="max-md:hidden">
        <div className="flex items-center gap-6 bg-bgLight px-6 py-3">
          {columns.map((col) => (
            <div key={col.key} className={col.headerClassName ?? col.className}>
              <Text variant="capsBig" tone="muted">{col.label}</Text>
            </div>
          ))}
        </div>

        {data.map((item) => (
          <div
            key={keyExtractor(item)}
            className={clsx(
              "flex items-center gap-6 border-t border-borderDefault px-6 py-4",
              onRowClick && "cursor-pointer transition-colors hover:bg-bgLight",
            )}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
          >
            {columns.map((col) => (
              <div key={col.key} className={col.className}>
                {col.render(item)}
              </div>
            ))}
          </div>
        ))}
      </div>

      <MobileRows
        columns={columns}
        data={data}
        keyExtractor={keyExtractor}
        onRowClick={onRowClick}
      />

      {footer && (
        <div className="border-t border-borderDefault px-6 py-4 max-md:px-4">
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * Phones: a row of columns doesn't fit 393px, so each row becomes a card —
 * the first column as its title (plus the next one when the first is an
 * unlabelled thumbnail), labelled fields two-up below, and the action
 * columns along the bottom.
 */
function MobileRows<T>({
  columns,
  data,
  keyExtractor,
  onRowClick,
}: Pick<SimpleTableProps<T>, "columns" | "data" | "keyExtractor" | "onRowClick">) {
  const leadCount = columns[0]?.label === "" && columns.length > 1 ? 2 : 1;
  const lead = columns.slice(0, leadCount);
  const rest = columns.slice(leadCount);
  // Unlabelled columns and "Actions" hold buttons: give them a full-width row.
  const isAction = (col: SimpleTableColumn<T>) => col.label === "" || /^actions?$/i.test(col.label);
  const fields = rest.filter((col) => !isAction(col));
  const extras = rest.filter(isAction);

  return (
    <div className="md:hidden">
      {data.map((item, i) => (
        <div
          key={keyExtractor(item)}
          className={clsx(
            "flex flex-col gap-3 px-4 py-4",
            i > 0 && "border-t border-borderDefault",
            onRowClick && "cursor-pointer transition-colors active:bg-bgLight",
          )}
          onClick={onRowClick ? () => onRowClick(item) : undefined}
        >
          <div className="flex min-w-0 items-center gap-3">
            {lead.map((col) => (
              <div key={col.key} className={clsx("min-w-0", col === lead[lead.length - 1] && "flex-1")}>
                {col.render(item)}
              </div>
            ))}
          </div>

          {fields.length > 0 && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {fields.map((col) => (
                <div key={col.key} className="flex min-w-0 flex-col gap-1 break-words">
                  <Text variant="capsSm" tone="muted">{col.label}</Text>
                  <div className="min-w-0">{col.render(item)}</div>
                </div>
              ))}
            </div>
          )}

          {extras.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {extras.map((col) => (
                <div key={col.key}>{col.render(item)}</div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
